import { useState } from "react";
import { useTranslation } from "react-i18next";
import { SlidersHorizontal, X } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { FieldLabel } from "@/components/ui/Input";
import { parseIntField, parseMoneyMajor } from "@/lib/moneyInput";

import { PRODUCT_ADVANCED_PARAMS } from "./productListLogic";

/** Shared with atomic filter clearing. */
export const ADVANCED_FILTER_PARAMS = PRODUCT_ADVANCED_PARAMS;
type Param = (typeof ADVANCED_FILTER_PARAMS)[number];

export interface AdvancedFilterValues {
  color?: string;
  size?: string;
  minPrice?: number;
  maxPrice?: number;
  minStock?: number;
  maxStock?: number;
  createdFrom?: number;
  createdTo?: number;
}

/**
 * Read the panel's params out of the URL into what `useProducts` wants.
 *
 * Anything unparseable is dropped rather than sent as 0 — a hand-edited or
 * truncated URL should fall back to "no filter", never to "price ≤ 0", which
 * silently returns an empty catalog and looks like data loss.
 */
export function readAdvancedFilters(params: URLSearchParams): AdvancedFilterValues {
  const money = (key: Param) => {
    const raw = params.get(key);
    if (!raw) return undefined;
    return parseMoneyMajor(raw).minor;
  };
  const int = (key: Param) => {
    const raw = params.get(key);
    if (!raw) return undefined;
    return parseIntField(raw).value;
  };
  // <input type="date"> gives YYYY-MM-DD with no timezone. Snap it to the
  // viewer's own start/end of day: a product created at 09:00 local on the 31st
  // must match "created on the 31st" for the person reading the screen, not for
  // UTC.
  const day = (key: Param, end: boolean) => {
    const raw = params.get(key);
    if (!raw) return undefined;
    const [y, m, d] = raw.split("-").map(Number);
    if (!y || !m || !d) return undefined;
    const at = end ? new Date(y, m - 1, d, 23, 59, 59, 999) : new Date(y, m - 1, d);
    return Number.isNaN(at.getTime()) ? undefined : at.getTime();
  };
  return {
    color: params.get("color") || undefined,
    size: params.get("size") || undefined,
    minPrice: money("minPrice"),
    maxPrice: money("maxPrice"),
    minStock: int("minStock"),
    maxStock: int("maxStock"),
    createdFrom: day("createdFrom", false),
    createdTo: day("createdTo", true),
  };
}

export function countAdvancedFilters(params: URLSearchParams): number {
  return ADVANCED_FILTER_PARAMS.filter((k) => params.get(k)).length;
}

export function ProductAdvancedFilters({
  params,
  onChange,
  onClear,
}: {
  params: URLSearchParams;
  onChange: (key: Param, value: string | null) => void;
  onClear: () => void;
}) {
  const { t } = useTranslation();
  const active = countAdvancedFilters(params);
  const [open, setOpen] = useState(active > 0);
  const value = (key: Param) => params.get(key) ?? "";

  // A range whose ends are the wrong way round returns nothing and looks like a
  // bug in the data. Say so on the field instead of leaving an empty table.
  const badMoneyRange =
    !!value("minPrice") && !!value("maxPrice") &&
    (parseMoneyMajor(value("minPrice")).minor ?? 0) > (parseMoneyMajor(value("maxPrice")).minor ?? 0);
  const badStockRange =
    !!value("minStock") && !!value("maxStock") &&
    (parseIntField(value("minStock")).value ?? 0) > (parseIntField(value("maxStock")).value ?? 0);

  const field = (key: Param, label: string, type: "text" | "number" | "date", invalid = false) => (
    <div className="min-w-0">
      <FieldLabel htmlFor={`pf-${key}`}>{label}</FieldLabel>
      <Input
        id={`pf-${key}`}
        type={type === "number" ? "text" : type}
        inputMode={type === "number" ? "decimal" : undefined}
        invalid={invalid}
        value={value(key)}
        onChange={(e) => onChange(key, e.target.value || null)}
      />
    </div>
  );

  return (
    <div className="w-full">
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          aria-controls="product-advanced-filters"
        >
          <SlidersHorizontal className="size-4" />
          {t("products.advancedFilters")}
          {active > 0 && (
            <span className="ml-1 rounded-full bg-brand px-1.5 py-0.5 text-2xs font-semibold text-white tabular">
              {active}
            </span>
          )}
        </Button>
        {active > 0 && (
          <Button variant="ghost" size="sm" onClick={onClear}>
            <X className="size-3.5" />
            {t("products.clearFilters")}
          </Button>
        )}
      </div>

      {open && (
        <div
          id="product-advanced-filters"
          className="mt-2 grid gap-3 rounded-sm border border-line bg-sunken p-3 sm:grid-cols-2 lg:grid-cols-4"
        >
          {field("color", t("products.color"), "text")}
          {field("size", t("products.size"), "text")}
          {field("minPrice", t("products.minPrice"), "number", badMoneyRange)}
          {field("maxPrice", t("products.maxPrice"), "number", badMoneyRange)}
          {field("minStock", t("products.minStock"), "number", badStockRange)}
          {field("maxStock", t("products.maxStock"), "number", badStockRange)}
          {field("createdFrom", t("products.createdFrom"), "date")}
          {field("createdTo", t("products.createdTo"), "date")}
          {(badMoneyRange || badStockRange) && (
            <p className="text-xs text-danger sm:col-span-2 lg:col-span-4">
              {t("products.rangeReversed")}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
